import React, { useEffect, useState, useRef } from 'react';
import MiscUtils from 'services/MiscUtils';
import Icon from 'components/Utils/NewIcon';
import { Device } from 'services/Native/Device';

interface DocumentPreviewProps {
  url?: string;
  originalUrl?: string;
  type?: string;
  blob?: Blob;
  fitWidth?: boolean;
  onClick?: () => void;
  locked?: boolean;
  unlockDateText?: string;
}

const DocumentPreview: React.FC<DocumentPreviewProps> = ({
  url: initialUrl,
  originalUrl,
  type: initialType,
  blob: initialBlob,
  fitWidth,
  onClick,
  locked,
  unlockDateText,
}) => {
  const [blob, setBlob] = useState<Blob | undefined>(initialBlob);
  const [type, setType] = useState<string | undefined>(initialType);
  const [url, setUrl] = useState<string | undefined>(initialUrl);
  const videoRef = useRef<HTMLVideoElement>(null);
  const blobUrlRef = useRef<string>();
  const isMobileDevice = Device.isMobile();

  useEffect(() => {
    if (locked) return;
    if (blob && !url) {
      const blobUrl = MiscUtils.getBlobUrl(blob);
      blobUrlRef.current = blobUrl;
      setUrl(blobUrl);
    }
  }, [url, blob, locked]);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (locked) return;
    if (!initialType && !blob && url) {
      MiscUtils.urlToBlob(url).then((fetchedBlob: Blob) => {
        setBlob(fetchedBlob);
        if (fetchedBlob.type) setType(fetchedBlob.type);
      });
    }
  }, [url, initialType, blob, locked]);

  useEffect(() => {
    if (locked) return;
    if ((type === 'video/webm' || type === 'video/mp4') && blob && videoRef.current) {
      const blobUrl = MiscUtils.getBlobUrl(blob);
      if (blobUrl) {
        videoRef.current.src = blobUrl;
      }
    }
  }, [type, blob, locked]);

  if (locked) {
    return (
      <div
        className="preview-panel preview-panel--locked"
        onClick={onClick}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          padding: 24,
          color: '#555',
          textAlign: 'center',
        }}
      >
        <Icon src="lock" width="40px" height="40px" />
        <span>
          {unlockDateText
            ? `This document will be available on ${unlockDateText}.`
            : 'This document is not yet available.'}
        </span>
      </div>
    );
  }

  const embedGoogle = isMobileDevice && originalUrl;

  return (
    !!url &&
    !!type && (
      <div className="preview-panel" key={url} onClick={onClick}>
        {type === 'application/pdf' &&
          (embedGoogle ? (
            <iframe
              className="preview-window"
              src={`https://drive.google.com/viewerng/viewer?embedded=true&url=${encodeURIComponent(
                originalUrl!,
              )}`}
              width="100%"
              height="90%"
            >
              <p>
                <em>There is content being displayed here that your browser doesn't support.</em>{' '}
                <a href={encodeURIComponent(originalUrl!)} target="_blank" rel="noreferrer">
                  {' '}
                  Please click here to attempt to view the information in a separate browser window.{' '}
                </a>{' '}
                Thanks for your patience!
              </p>
            </iframe>
          ) : (
            <iframe
              className="preview-window"
              aria-labelledby={url}
              src={`${url}${fitWidth ? '#view=fitH' : ''}`}
            />
          ))}
        {(type || '').startsWith('image/') && <img src={url} alt={url} />}
        {(type === 'video/webm' || type === 'video/mp4') && (
          <video
            ref={videoRef}
            autoPlay
            controls
            style={{
              margin: 'auto',
              display: 'block',
              maxWidth: '100%',
              maxHeight: '100%',
            }}
          />
        )}
      </div>
    )
  );
};

export default DocumentPreview;
